import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import NotFound from "@/pages/NotFound";
import AccessDenied from "@/pages/AccessDenied";
import { AdminAppShell } from "../../design-system/shells/AdminAppShell";
import { useAuth } from "../../lib/auth";
import { api } from "../../lib/api";
import { errMsg } from "../../lib/portfolio";
import { usePhaseFeature } from "../../lib/phaseFeatures";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent } from "../../components/ui/card";

const money = (c) => `$${(c / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const toCents = (s) => Math.round(Number(String(s).replace(/[$,]/g, "")) * 100);
export const PROPERTY_ROLES = ["OWNER_SUPER_ADMIN", "ADMINISTRATOR", "PROPERTY_MANAGER"];
const NEXT = { submitted: "Triage", triaged: "Create work order", work_order: "Mark completed" };

export function PropertyGate({ children }) {
  const { user } = useAuth();
  const f = usePhaseFeature(7, user?.id);
  if (!f) return <div className="p-8 text-sm font-nav text-erf-dark/70" data-testid="phase7-gate-loading">Loading…</div>;
  if (!PROPERTY_ROLES.includes(user?.role_key)) return <AccessDenied />; // B2a: role check first → 403 page (no module-state leak)
  if (!f.enabled) return <NotFound />;
  return children;
}

const Shell = ({ title, subtitle, crumbs, children }) => {
  const { user } = useAuth();
  return <AdminAppShell activeNav="property" user={user} title={title} subtitle={subtitle} breadcrumbs={crumbs}>{children}</AdminAppShell>;
};

export function PropertyList() {
  const [rows, setRows] = useState([]);
  const [dash, setDash] = useState(null);
  useEffect(() => { api.get("/phase7/dashboard").then((r) => setDash(r.data)); }, [rows]);
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [error, setError] = useState(null);
  const load = useCallback(() => api.get("/phase7/properties").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const add = (e) => {
    e.preventDefault();
    api.post("/phase7/properties", { name, address }).then(() => { setName(""); setAddress(""); setError(null); load(); })
      .catch((err) => setError(errMsg(err, "Could not add the property.")));
  };
  return (
    <Shell title="Property Management" subtitle="Properties, units, lease records and maintenance."
      crumbs={[{ label: "Owner Command Center", to: "/owner" }, { label: "Property Management", to: null }]}>
      <div data-testid="phase7-workspace">
        <p className="mb-4 text-sm font-nav text-erf-dark/80" data-testid="phase7-boundary-note">Lease records only. No rent is collected and no notices are sent. Leases never renew automatically.</p>
        {dash && dash.properties.length > 0 && (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3 mb-5" data-testid="phase7-dashboard">
            {dash.properties.map((d) => (
              <div key={d.property_id} className="rounded-xl border border-erf-borderCream bg-white p-4 text-sm font-nav text-erf-dark" data-testid={`phase7-dash-${d.property_id}`}>
                <div className="font-display font-semibold">{d.name}</div>
                <div>Occupancy {d.occupied}/{d.units} ({d.occupancy_pct}%)</div>
                <div>Maintenance: {d.maintenance_backlog.submitted} new · {d.maintenance_backlog.triaged} triaged · {d.maintenance_backlog.work_order} with vendor</div>
                <div>Leases ending in 60 days: {d.leases_ending_60d.length === 0 ? "none" : d.leases_ending_60d.map((l) => `${l.tenant_name} (${l.end_date})`).join(", ")}</div>
              </div>
            ))}
          </div>
        )}
        <form onSubmit={add} className="flex flex-wrap items-end gap-3 mb-5" data-testid="phase7-property-form">
          <label className="text-sm font-nav text-erf-dark">Property name<Input required value={name} onChange={(e) => setName(e.target.value)} className="mt-1 h-11" data-testid="phase7-property-name" /></label>
          <label className="text-sm font-nav text-erf-dark">Address<Input value={address} onChange={(e) => setAddress(e.target.value)} className="mt-1 h-11" data-testid="phase7-property-address" /></label>
          <Button type="submit" className="h-11 bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid="phase7-property-add">Add property</Button>
          {error && <p role="alert" className="w-full text-sm text-[#991B1B]" data-testid="phase7-property-error">{error}</p>}
        </form>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="phase7-property-grid">
          {rows.length === 0 && <p className="text-sm font-nav text-erf-dark">No properties yet.</p>}
          {rows.map((p) => (
            <Link key={p.id} to={`/phase7/properties/${p.id}`} data-testid={`phase7-property-card-${p.id}`}
              className="ds-card block rounded-xl border border-erf-borderCream bg-[#FEFCF7] p-4 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-erf-gold">
              <div className="font-display font-semibold text-erf-dark">{p.name}</div>
              <div className="text-sm font-nav text-erf-dark/80">{p.unit_count} units · {p.open_requests} open requests</div>
            </Link>
          ))}
        </div>
      </div>
    </Shell>
  );
}

function UnitRow({ u, reload, setError }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ tenant_name: "", start_date: "", end_date: "", rent: "", deposit: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = (e) => {
    e.preventDefault();
    api.post(`/phase7/units/${u.id}/leases`, { tenant_name: f.tenant_name, start_date: f.start_date, end_date: f.end_date,
      monthly_rent_cents: toCents(f.rent || 0), deposit_cents: toCents(f.deposit || 0) })
      .then(() => { setOpen(false); setError(null); reload(); }).catch((err) => setError(errMsg(err, "Could not record the lease.")));
  };
  return (
    <li className="rounded-lg border border-erf-borderCream p-3" data-testid={`phase7-unit-${u.label}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="mr-auto font-semibold">Unit {u.label}{u.building ? ` · Bldg ${u.building}` : ""}</span>
        <span>{u.active_lease ? `${u.active_lease.tenant_name} · ${money(u.active_lease.monthly_rent_cents)}/mo until ${u.active_lease.end_date}` : "Vacant"}</span>
        <Button variant="outline" className="h-11 border-erf-borderCream" aria-expanded={open} onClick={() => setOpen(!open)} data-testid={`phase7-lease-toggle-${u.label}`}>Record lease</Button>
      </div>
      {open && (
        <form onSubmit={save} className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <label>Tenant name<Input required value={f.tenant_name} onChange={set("tenant_name")} className="mt-1 h-11" data-testid={`phase7-lease-name-${u.label}`} /></label>
          <label>Start<Input type="date" required value={f.start_date} onChange={set("start_date")} className="mt-1 h-11" data-testid={`phase7-lease-start-${u.label}`} /></label>
          <label>End<Input type="date" required value={f.end_date} onChange={set("end_date")} className="mt-1 h-11" data-testid={`phase7-lease-end-${u.label}`} /></label>
          <label>Monthly rent ($)<Input required inputMode="decimal" value={f.rent} onChange={set("rent")} className="mt-1 h-11" data-testid={`phase7-lease-rent-${u.label}`} /></label>
          <label>Deposit ($)<Input inputMode="decimal" value={f.deposit} onChange={set("deposit")} className="mt-1 h-11" data-testid={`phase7-lease-deposit-${u.label}`} /></label>
          <Button type="submit" className="h-11 bg-erf-dark text-[#FEFCF7]" data-testid={`phase7-lease-save-${u.label}`}>Save lease record</Button>
        </form>
      )}
    </li>
  );
}

function Maintenance({ p, reload, setError }) {
  const [unitId, setUnitId] = useState(p.units[0]?.id || "");
  const [summary, setSummary] = useState("");
  const [vendor, setVendor] = useState("");
  const [reason, setReason] = useState("");
  const run = (pr) => pr.then(() => { setError(null); reload(); }).catch((e) => setError(errMsg(e, "That did not work.")));
  return (
    <>
      <h2 className="font-display font-semibold text-base md:text-lg">Maintenance</h2>
      {p.units.length > 0 && (
        <form className="flex flex-wrap items-end gap-3" onSubmit={(e) => { e.preventDefault(); run(api.post("/phase7/maintenance", { unit_id: unitId, summary }).then(() => setSummary(""))); }}>
          <label>Unit
            <select value={unitId} onChange={(e) => setUnitId(e.target.value)} className="mt-1 block h-11 rounded-md border border-erf-borderCream bg-white px-3" data-testid="phase7-mr-unit">
              {p.units.map((u) => <option key={u.id} value={u.id}>Unit {u.label}</option>)}
            </select>
          </label>
          <label>What needs fixing<Input required value={summary} onChange={(e) => setSummary(e.target.value)} className="mt-1 h-11" data-testid="phase7-mr-summary" /></label>
          <Button type="submit" className="h-11 bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid="phase7-mr-add">Log request</Button>
        </form>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <label>Vendor or crew (needed for a work order)<Input value={vendor} onChange={(e) => setVendor(e.target.value)} className="mt-1 h-11" data-testid="phase7-mr-vendor" /></label>
        <label>Reason for cancelling (at least 10 characters)<Input value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1 h-11" data-testid="phase7-mr-reason" /></label>
      </div>
      <ul className="space-y-2">
        {p.maintenance.length === 0 && <li>No maintenance requests.</li>}
        {p.maintenance.map((r) => (
          <li key={r.id} className="flex flex-wrap items-center gap-2" data-testid={`phase7-mr-${r.id}`}>
            <span className="mr-auto">Unit {r.unit_label}: {r.summary} — {r.state.replace("_", " ")}{r.vendor_name ? ` · ${r.vendor_name}` : ""}</span>
            {NEXT[r.state] && <Button variant="outline" className="h-11 border-erf-borderCream" onClick={() => run(api.post(`/phase7/maintenance/${r.id}/advance`, { vendor_name: vendor }))} data-testid={`phase7-mr-advance-${r.id}`}>{NEXT[r.state]}</Button>}
            {NEXT[r.state] && <Button variant="outline" className="h-11 border-erf-borderCream" onClick={() => run(api.post(`/phase7/maintenance/${r.id}/cancel`, { reason }))} data-testid={`phase7-mr-cancel-${r.id}`}>Cancel</Button>}
          </li>
        ))}
      </ul>
    </>
  );
}

export function PropertyDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [label, setLabel] = useState("");
  const [error, setError] = useState(null);
  const load = useCallback(() => api.get(`/phase7/properties/${id}`).then((r) => setP(r.data)).catch((e) => setError(errMsg(e, "Could not load."))), [id]);
  useEffect(() => { load(); }, [load]);
  return (
    <Shell title={p?.name || "Property"} subtitle={p?.address || ""} crumbs={[{ label: "Property Management", to: "/phase7/properties" }, { label: p?.name || "Property", to: null }]}>
      <div data-testid="phase7-property-detail">
        {error && <p role="alert" className="mb-3 text-sm text-[#991B1B]" data-testid="phase7-detail-error">{error}</p>}
        {p && (
          <Card className="ds-card border-erf-borderCream">
            <CardContent className="p-5 space-y-4 text-sm font-nav text-erf-dark">
              <h2 className="font-display font-semibold text-base md:text-lg">Units</h2>
              <form className="flex flex-wrap items-end gap-3" onSubmit={(e) => { e.preventDefault(); api.post(`/phase7/properties/${id}/units`, { label }).then(() => { setLabel(""); setError(null); load(); }).catch((err) => setError(errMsg(err, "Could not add the unit."))); }}>
                <label>Unit label<Input required value={label} onChange={(e) => setLabel(e.target.value)} className="mt-1 h-11" data-testid="phase7-unit-label" /></label>
                <Button type="submit" className="h-11 bg-erf-dark text-[#FEFCF7]" data-testid="phase7-unit-add">Add unit</Button>
              </form>
              <ul className="space-y-2" data-testid="phase7-unit-list">{p.units.map((u) => <UnitRow key={u.id} u={u} reload={load} setError={setError} />)}</ul>
              <Maintenance key={p.units.length} p={p} reload={load} setError={setError} />
            </CardContent>
          </Card>
        )}
      </div>
    </Shell>
  );
}
